/* WorkloadMeter — semicircular gauge of "your weekly review capacity".
   Shows used/cap, with the arc segment colored. */

import type { TaskItem } from '@/modules/shared/types/yc-demo';

export interface WorkloadMeterProps {
    tasks: TaskItem[];
    capacity?: number;
    accent?: string;
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
}

export function WorkloadMeter({
    tasks,
    capacity = 50,
    accent = '#fd775e',
    surface = 'rgba(255,255,255,0.7)',
    text = '#2a241e',
    muted = '#9c948a',
    rule = 'rgba(120, 90, 50, 0.08)',
}: WorkloadMeterProps) {
    const used = Math.min(tasks.length + 9, capacity); // simulate weekly load
    const pct = used / capacity;
    const radius = 60;
    const cx = 75;
    const cy = 70;
    const startAngle = Math.PI;
    const endAngle = Math.PI + Math.PI * pct;
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;

    return (
        <div
            style={{
                background: surface,
                borderRadius: 18,
                padding: 18,
                border: `1px solid ${rule}`,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div style={{ fontSize: 13, fontWeight: 600, color: text, marginBottom: 4 }}>Weekly load</div>
            <div style={{ fontSize: 11, color: muted, marginBottom: 6 }}>your review queue</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
                <svg width={150} height={84} viewBox="0 0 150 84">
                    {/* track */}
                    <path
                        d={`M 15 70 A ${radius} ${radius} 0 0 1 135 70`}
                        fill="none"
                        stroke={rule}
                        strokeWidth={9}
                        strokeLinecap="round"
                    />
                    {/* progress arc */}
                    {pct > 0 && (
                        <path
                            d={`M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`}
                            fill="none"
                            stroke={accent}
                            strokeWidth={9}
                            strokeLinecap="round"
                        />
                    )}
                    <text
                        x={75}
                        y={62}
                        textAnchor="middle"
                        fontFamily="var(--v2-font-serif)"
                        fontSize={28}
                        fontWeight={400}
                        fill={text}
                        letterSpacing="-0.02em"
                    >
                        {used}
                    </text>
                    <text
                        x={75}
                        y={78}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={600}
                        fill={muted}
                        letterSpacing="0.06em"
                        style={{ textTransform: 'uppercase' }}
                    >
                        of {capacity} this week
                    </text>
                </svg>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Pace</div>
                    <div style={{ fontSize: 14, color: text, fontWeight: 600, marginBottom: 8 }}>
                        {pct < 0.6 ? 'Comfortable' : pct < 0.85 ? 'On track' : 'Heavy'}
                    </div>
                    <div style={{ fontSize: 11, color: muted }}>{capacity - used} more before cap</div>
                </div>
            </div>
        </div>
    );
}
